import {
  Card,
  CardBody,
  Container,
  CardHeader,
  Stack,
  IconButton,
  useDisclosure,
  Button,
  Modal,
  ModalBody,
  ModalCloseButton,
  ModalContent,
  ModalFooter,
  ModalHeader,
  ModalOverlay,
  Heading,
  Input,
  Divider,
  Tabs,
  TabList,
  Tab,
  TabPanels,
  TabPanel,
  Checkbox,
  FormControl,
  FormLabel,
  CheckboxGroup,
  useToast, InputGroup,
  InputRightElement,
  Tag
} from "@chakra-ui/react";
import MyBreadcrumb from "components/breadcrumb/Breadcrumb";
import CustomTable from "components/table/CustomTable";
import { DeleteIcon, EditIcon, SmallAddIcon, ViewIcon } from "@chakra-ui/icons";
import { createColumnHelper } from "@tanstack/react-table";
import { ReactElement, useEffect, useState } from "react";
import PermissionsForm from "./modalRole/RoleFormPermissions";
import TargetsFilter from "components/form/TargetsFilter";
import TargetFilter from "../target/components/targetFilter/TargetFilter";
import { Target } from "types/Target";
import { SubTarget } from "types/SubTarget";
import { PermissionModalMode } from "types/PermissionModalMode";
import { Controller, SubmitHandler, useForm } from "react-hook-form";
import RoleModalTitle from "./modalRole/RoleModalTitle";
import RoleFormInfo from "./modalRole/RoleFormInfo";
import { Role } from "types/Role";
import { useSelector } from "react-redux";
import { isCodeInUserRoles } from "utils/utils";
import { deleteUser, patchUserRoles, postCreateUser } from "service/api/user.api";
import { deleteUser as deleteUserAction } from "store/reducers/auth.slice";
import { addSimpleUser, updateUserRoles } from "store/reducers/auth.slice";
import { useAppDispatch } from "store/store.hook";

import DocumentFilter from "../target/components/documentFilter/DocumentFilter";
import { Document } from "types/Document";
import { Breadcrumb } from "types/Breadcrumb";

type TableData = {
  _id: string;
  title: string;
  description: string;
  code: string;
  permissions: any[];
};

type TableDataUsers = {
  _id: string;
  email: string;
  roles: string[];
};

const columnHelperUsers = createColumnHelper<TableDataUsers>();


const columnHelper = createColumnHelper<TableData>();

type RolesProps = {
  isUserAdmin: boolean;
  isUserAdminRolesEditor: boolean;
};

const getDefaultValues = (roles: Role[]) => {
  let defaultValues = {} as any
  for (let i = 0; i < roles.length; i++) {
    defaultValues[`${roles[i]._id}`] = false
  }
  return defaultValues
}


/**
 * Roles component for managing roles, permissions, and users.
 * 
 * @param {RolesProps} props - The properties for the Roles component.
 * @param {boolean} props.isUserAdmin - Indicates if the current user is an admin.
 * @param {boolean} props.isUserAdminRolesEditor - Indicates if the current user can edit roles.
 * 
 * @returns {JSX.Element} The rendered Roles component.
 * 
 * @component
 * 
 * @example
 * return (
 *   <Roles isUserAdmin={true} isUserAdminRolesEditor={true} />
 * )
 * 
 * @remarks
 * This component uses various hooks and state management to handle the display and modification of roles and users.
 * It includes modals for creating, editing, viewing, and deleting roles and users.
 * 
 * @hook
 * useSelector - To select state from the Redux store.
 * useDisclosure - To manage the open/close state of modals.
 * useForm - To manage form state and validation.
 * useToast - To display toast notifications.
 * useAppDispatch - To dispatch actions to the Redux store.
 * useEffect - To handle side effects.
 * 
 * @state {any} userFormValues - The form values for the user.
 * @state {Document | null} document - The selected document.
 * @state {Target[]} targets - The list of targets.
 * @state {Target | null} target - The selected target.
 * @state {SubTarget} subTarget - The selected sub-target.
 * @state {PermissionModalMode} modalMode - The mode of the permission modal.
 * @state {string} roleTitle - The title of the role.
 * @state {string} roleDescription - The description of the role.
 * @state {string} roleCode - The code of the role.
 * @state {string} roleId - The ID of the role.
 * @state {any[]} rolePermissions - The permissions of the role.
 * @state {PermissionModalMode} modalModeUsers - The mode of the user modal.
 * @state {string} userId - The ID of the user.
 * @state {number} tabsIndex - The index of the selected tab.
 * @state {boolean} show - The state to show/hide password.
 * 
 * @function handleClick - Toggles the show state for password visibility.
 * @function showFormModal - Displays the role form modal with the specified mode and role details.
 * @function showUserModal - Displays the user modal with the specified mode and user details.
 * @function handlerOnChangeSubTarget - Handles the change event for the sub-target selection.
 * @function handlerOnChangeLabel - Handles the change event for the label selection.
 * @function handlerOnChangeDocument - Handles the change event for the document selection.
 * @function handlerOnChangeTarget - Handles the change event for the target selection.
 * @function onSubmit - Handles the form submission for creating, editing, or deleting users.
 * 
 * @constant {Breadcrumb} breadcrumb - The breadcrumb navigation for the component.
 */
export default function Roles({ isUserAdmin, isUserAdminRolesEditor }: RolesProps) {



  const documents = useSelector((state: any) => state.documents.documents);
  const roles = useSelector((state: any) => state.roles.roles);
  const roles_without_admin = useSelector((state: any) => state.roles.roles.filter((role: any) => role.code !== "ADMIN"));
  const users = useSelector((state: any) => state.user.users);
  const roleInfo = useSelector((state: any) => state.roles.tmp);


  const { isOpen, onOpen, onClose } = useDisclosure()
  const [userFormValues, setUserFormValues] = useState<any>({});
  const { isOpen: isOpenUser, onOpen: onOpenUser, onClose: onCloseUser } = useDisclosure()

  const { register, handleSubmit, control, reset } = useForm({
    defaultValues: {
      email: "",
      _id: "",
      isAdmin: false,
      roles: getDefaultValues(roles)
    },
    values: userFormValues,
  });

  const toast = useToast()
  const dispatch = useAppDispatch();

  const [document, setDocument] = useState<Document>(null)
  const [targets, setTargets] = useState<Target[]>([])
  const [target, setTarget] = useState<Target>(null)
  const [subTarget, setSubTarget] = useState<SubTarget>({} as SubTarget)
  const [modalMode, setModalMode] = useState<PermissionModalMode>(0)
  const [roleTitle, setRoleTitle] = useState<string>("")
  const [roleDescription, setRoleDescription] = useState<string>("")
  const [roleCode, setRoleCode] = useState<string>("")
  const [roleId, setRoleId] = useState<string>("")
  const [rolePermissions, setRolePermissions] = useState<any[]>([])

  const [modalModeUsers, setModalModeUsers] = useState<PermissionModalMode>(0)
  const [userId, setUserId] = useState<string>("")
  const [tabsIndex, setTabsIndex] = useState<number>(0)


  const [show, setShow] = useState(false)
  const handleClick = () => setShow(!show)

  const showFormModal = (mode: PermissionModalMode, _id: string = "", title: string = "", description = "", role = "", permissions: string[] = []) => {
    setModalMode(mode)
    setRoleTitle(title)
    setRoleDescription(description)
    setRoleCode(role)
    setRoleId(_id)
    setRolePermissions(permissions)
    onOpen()
  }

  const showUserModal = (mode: PermissionModalMode, _id: string = "") => {

    setModalModeUsers(mode)
    if (mode === PermissionModalMode.Create) {
      reset({})
      setUserFormValues({ email: "", roles: [] })
      setUserId("")
    }
    else {
      const user = users.find((user: any) => user._id == _id)
      const email = user?.email
      const rolesOfUser = user?.roles
      setUserId(_id)
      setUserFormValues({ email: email, roles: rolesOfUser })
    }

    onOpenUser()
  }


  useEffect(() => {
    if (!isOpen) {
      setTarget(null)
      setSubTarget(null)
    }
  }, [isOpen])

  useEffect(() => {
    setSubTarget(null)
  }, [target])

  const columns = [
    columnHelper.accessor("title", {
      cell: (info: any) => info.getValue(),
      header: `Titolo Ruolo`,
    }),
    columnHelper.accessor("description", {
      cell: (info: any) => info.getValue(),
      header: `Descrizione ruolo`,
    }),
    columnHelper.accessor("code", {
      cell: (info: any) => info.getValue(),
      header: `Codice ruolo`,
    }),
    columnHelper.accessor("_id", {
      cell: ({ row }) => (
        <Stack spacing={2} direction="row" align="center">
          <IconButton
            colorScheme="blue"
            aria-label="View"
            size="sm"
            onClick={() => showFormModal(PermissionModalMode.View, row.original._id, row.original.title, row.original.description, row.original.code, row.original.permissions
            )}
            icon={<ViewIcon />}
          />
          <IconButton
            colorScheme="yellow"
            aria-label="Edit"
            size="sm"
            isDisabled={!isUserAdminRolesEditor}
            onClick={() => showFormModal(PermissionModalMode.Edit, row.original._id, row.original.title, row.original.description, row.original.code, row.original.permissions
            )}
            icon={<EditIcon />}
          />
          <IconButton
            colorScheme="red"
            aria-label="Delete"
            size="sm"
            isDisabled={!isUserAdminRolesEditor}
            onClick={() => showFormModal(PermissionModalMode.Delete, row.original._id, row.original.title, row.original.description, row.original.code, row.original.permissions)}
            icon={<DeleteIcon />}
          />
        </Stack>
      ),
      header: ``,
      enableSorting: false,
      enableColumnFilter: false,
      enableGlobalFilter: false,
    }),
  ];

  const columns_users = [
    columnHelperUsers.accessor("email", {
      cell: (info: any) => info.getValue(),
      header: `Email`,
    }),
    columnHelperUsers.accessor("_id", {
      cell: ({ row }) => (
        <Stack spacing={2} direction="row" align="center">
          <IconButton
            colorScheme="blue"
            aria-label="View"
            size="sm"
            onClick={() => showUserModal(PermissionModalMode.View, row.original._id
            )}
            icon={<ViewIcon />}
          />
          <IconButton
            colorScheme="yellow"
            aria-label="Edit"
            size="sm"
            isDisabled={!isUserAdminRolesEditor}
            onClick={() => showUserModal(PermissionModalMode.Edit, row.original._id
            )}
            icon={<EditIcon />}
          />
          <IconButton
            colorScheme="red"
            aria-label="Edit"
            size="sm"
            isDisabled={!isUserAdminRolesEditor}
            onClick={() => showUserModal(PermissionModalMode.Delete, row.original._id
            )}
            icon={<DeleteIcon />}
          />
        </Stack>
      ),
      header: ``,
      enableSorting: false,
      enableColumnFilter: false,
      enableGlobalFilter: false,
    }),
  ];


  const handlerOnChangeSubTarget = (event: any) => {
    setSubTarget(target?.subtargets.find((subtarget: SubTarget) => subtarget._id == event.target.value));
  };

  const handlerOnChangeLabel = (event: any) => {
    setSubTarget(target?.subtargets.find((subtarget: SubTarget) => subtarget._id == event.target.value));
  };

  const handlerOnChangeDocument = (event: any) => {

    const document = documents.find(
      (document: Document) => document._id == (event.target.value)
    )
    setDocument(document)
    setTargets(document?.targets)

  };





  const handlerOnChangeTarget = (event: any) => {
    setTarget(
      targets?.find(
        (target: { id: any }) => target.id == event.target.value
      )
    );
  };


  const onSubmit: SubmitHandler<any> = (data) => {

    if (modalModeUsers == PermissionModalMode.Create) {

      const roles = Object.keys(data.roles).filter((key) => data[key] === true)
      const isAdmin = roles.includes('ADMIN')

      postCreateUser(data.email, data.password, isAdmin, roles).then((res) => {

        dispatch(addSimpleUser({
          email: res.email,
          roles: res.roles,
          isAdmin: res.isAdmin,
          _id: res._id
        }));

        setUserFormValues({ email: "", roles: [] })

        toast({
          title: "Utente creato con successo",
          status: "success",
          duration: 6000,
          isClosable: true,
        })

      }).catch((err) => {
        toast({
          title: "Errore durante la creazione dell'utente",
          status: "error",
          duration: 6000,
          isClosable: true,
        })

      })

    } else if (modalModeUsers == PermissionModalMode.Edit) {

      let roles = [] as string[]

      for (const [key, value] of Object.entries(data.roles)) {
        if (value == true) {
          roles.push(key)
        }
      }

      patchUserRoles(userId, roles).then((res) => {
        dispatch(updateUserRoles({
          userId: userId,
          roles: res.roles
        }));

        setUserFormValues({ email: "", roles: [] })

        toast({
          title: "Ruoli utente modificati con successo",
          status: "success",
          duration: 6000,
          isClosable: true,
        })

      }).catch((err) => {
        toast({
          title: "Errore durante la modifica dei ruoli dell'utente",
          status: "error",
          duration: 6000,
          isClosable: true,
        })
      })


    } else if (modalModeUsers == PermissionModalMode.Delete) {

      deleteUser(userId).then((res) => {

        dispatch(deleteUserAction({ userId: userId }));

        setUserFormValues({ email: "", roles: [] })

        toast({
          title: "Utente eliminato con successo",
          status: "success",
          duration: 6000,
          isClosable: true,
        })

      }).catch((err) => {
        toast({
          title: "Errore: utente non eliminato",
          status: "error",
          duration: 6000,
          isClosable: true,

        })
      })

    }
    onCloseUser()
  }

  const breadcrumb: Breadcrumb = new Breadcrumb(
    [
      { label: "Ruoli", link: "/roles", value: "", isActive: true },
    ]
  );
  

  return (
    <>
      <Modal isOpen={isOpenUser} onClose={onCloseUser} size={"xl"}>
        <ModalOverlay />
        <ModalContent>
          <form onSubmit={handleSubmit(onSubmit)} >
            <ModalHeader>
              Ruoli assegnati all'utente {userFormValues.email}
            </ModalHeader>
            <ModalCloseButton />
            <ModalBody>
              <Stack direction="column">

                <FormControl mb={5} isRequired>
                  <FormLabel>Email</FormLabel>
                  <Input type="email" placeholder="Inserisci l'email" size='md' required={true}
                    isReadOnly={modalModeUsers != PermissionModalMode.Create}
                    isRequired={true}
                    defaultValue={userFormValues.email}
                    autoComplete="email"
                    {
                    ...register(`email`, { required: true })
                    }
                  />
                </FormControl>
                {
                  modalModeUsers === PermissionModalMode.Create &&
                  <FormControl mb={5} isRequired>
                    <FormLabel>Password</FormLabel>
                    <InputGroup size='md'>
                      <Input
                        pr='4.5rem'
                        type={show ? 'text' : 'password'}
                        placeholder='Enter password'
                        autoComplete="current-password"
                        {
                        ...register(`password`, { required: true })
                        }
                      />
                      <InputRightElement width='4.5rem'>
                        <Button h='1.75rem' size='sm' onClick={handleClick}>
                          {show ? 'Hide' : 'Show'}
                        </Button>
                      </InputRightElement>
                    </InputGroup>
                  </FormControl>
                }

                {
                  modalModeUsers !== PermissionModalMode.Create &&
                  <FormControl mb={5}>
                    <FormLabel>

                      Ruoli

                    </FormLabel>

                    <Stack>
                      <CheckboxGroup isDisabled={modalModeUsers == PermissionModalMode.View || modalModeUsers == PermissionModalMode.Delete} >

                        {
                          roles.map(
                            (role: any, index: any): ReactElement => {
                              return (
                                <Controller
                                  control={control}
                                  name={`roles.${role._id}`}
                                  key={`roles.${role._id}`}
                                  defaultValue={isCodeInUserRoles(role._id, userId, users, modalModeUsers)}
                                  render={({ field: { onChange, value, ref } }) => (
                                    <>
                                      <Checkbox
                                        onChange={onChange}
                                        textTransform="capitalize"
                                        ref={ref}
                                        isChecked={value}
                                        id={`roles.${role._id}.${index}`}
                                        isReadOnly={role.code == 'ADMIN' && value==true}
                                        cursor={value==true && role.code == 'ADMIN' ? 'not-allowed' : 'pointer'}
                                      >
                                        {role.code} - {role.title}
                                        {
                                          (value && role.code == 'ADMIN') &&
                                          <Tag as="span" colorScheme={"red"} ml={1}>
                                            Ruolo non modificabile
                                          </Tag>
                                        }
                                      </Checkbox>
                                    </>
                                  )}
                                />
                              );
                            }
                          )}
                      </CheckboxGroup>
                    </Stack>
                  </FormControl>
                }
              </Stack>
            </ModalBody>

            <ModalFooter>
              <Button mr={3} onClick={onCloseUser}>
                Chiudi
              </Button>
              {
                modalModeUsers === PermissionModalMode.Create &&
                <Button colorScheme="green" mr={3} type="submit">
                  Salva nuovo utente
                </Button>
              }
              {
                modalModeUsers === PermissionModalMode.Edit &&
                <Button colorScheme="green" mr={3} type="submit" >
                  Salva modifiche
                </Button>
              }
              {
                modalModeUsers === PermissionModalMode.Delete &&
                <Button colorScheme="red" mr={3} type="submit" >
                  Elimina utente
                </Button>
              }
            </ModalFooter>
          </form>
        </ModalContent>
      </Modal >


      <Modal isOpen={isOpen} onClose={onClose} size={"6xl"}>
        <ModalOverlay />
        <ModalContent>
          <ModalHeader>
            <RoleModalTitle modalMode={modalMode} roleTitle={roleTitle} />
          </ModalHeader>
          <ModalCloseButton />
          <ModalBody>
            <RoleFormInfo modalMode={modalMode} roleTitle={roleTitle} roleDescription={roleDescription} roleCode={roleCode} roleId={roleId} onClose={onClose} />

            <Divider orientation='horizontal' variant={"solid"} my={2} />

            {
              (modalMode !== PermissionModalMode.Create && modalMode !== PermissionModalMode.Delete)
              &&
              <>

                <DocumentFilter documents={documents} handleOnChangeDocument={handlerOnChangeDocument} />
                <TargetsFilter targets={targets} placeholderTarget={"Seleziona l'obiettivo"} handlerOnChangeTarget={handlerOnChangeTarget} />

                <TargetFilter
                  target={target}
                  subTargetFilter={true}
                  timePeriods={[]}
                  labelFilter={false}
                  placeholderSubTarget="Seleziona sotto-obiettivo da visualizzare"
                  placeholderLabel="Seleziona anno di riferimento da visualizzare"
                  handlerOnChangeSubTarget={handlerOnChangeSubTarget}
                  handlerOnChangeLabel={handlerOnChangeLabel}
                  tabIndex={0}
                  selectedLabel=""
                  selectedSubTarget={subTarget?._id}
                />
                <Divider orientation="horizontal" variant={"solid"} my={2} />
                <PermissionsForm documentId={document?._id} target={target} subTarget={subTarget} modalMode={modalMode} roleId={roleId} roleTitle={roleInfo?.roleTitle} roleDescription={roleInfo?.roleDescription} roleCode={roleInfo?.roleCode} rolePermissions={rolePermissions} onClose={onClose} />
              </>
            }

          </ModalBody>

        </ModalContent>
      </Modal>

      <section className="dashboard-first-section">
        <Container maxW="container.lg" maxH={"container.xl"} textAlign={"left"}>
          <MyBreadcrumb breadcrumb={breadcrumb}/>


          <Card minH={"60vh"}>
            <CardHeader>
              <Heading as="h1" size="xl" >
                Gestione Ruoli, Permessi e Utenti
              </Heading>

              {
                tabsIndex == 0 ?
                  <Button colorScheme="green" alignContent={"end"} float={"right"} onClick={() => showFormModal(PermissionModalMode.Create)} size={"xs"}>
                    Aggiungi ruolo <SmallAddIcon />
                  </Button> : <Button colorScheme="green" alignContent={"end"} float={"right"} onClick={() => showUserModal(PermissionModalMode.Create)} size={"xs"}>
                    Aggiungi utente <SmallAddIcon />
                  </Button>
              }


            </CardHeader>
            <CardBody>

              <Tabs onChange={(index) => setTabsIndex(index)}>
                <TabList>
                  <Tab>Ruoli e permessi</Tab>
                  <Tab>Ruoli e Utenti</Tab>
                </TabList>

                <TabPanels>
                  <TabPanel>
                    <CustomTable data={roles_without_admin} columns={columns} />
                  </TabPanel>
                  <TabPanel>
                    <CustomTable data={users} columns={columns_users} />
                  </TabPanel>
                </TabPanels>
              </Tabs>


            </CardBody>
          </Card>
        </Container>
      </section>
    </>
  );
}